/**
 * WCAG relative-luminance contrast formula, shared by every place that needs
 * to pick readable text on a customer color. Rule + binding test vectors:
 * docs/superpowers/specs/2026-08-18-color-round-design.md §A1.
 *
 * Five places mirror this exact formula (coefficients must match byte-for-
 * byte, spec §A2): this file (ciTokens.js imports it), the PHP mirror
 * (publishedData.php's wm_parseColorToRgb/wm_relativeLuminance/
 * wm_contrastTextColor), useCorporateIdentity.ts (V2 editor dye), keditor.js
 * (V1 editor dye) and lib.utils.js (V1 viewer dye).
 *
 * Plain JS (not TS, persist-once spec 2026-08-18-token-css-persist-once
 * Task 2): ciTokens.js's generateTokenCss() must resolve inside the Vue-2
 * webpack build (ProjectSettings.vue/FolderSettings.vue import it via
 * tokenCssPersist.js), which cannot bundle .ts files — precedent:
 * cssEmitter.js, elementSchemas.js. Types live in wcagContrast.d.ts.
 */

const DARK = '#212529';
const DARK_RGB = { r: 33, g: 37, b: 41 };

const RGB_RE = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*[\d.]+\s*)?\)$/i;
const HSL_RE = /^hsla?\(\s*([\d.]+)(?:deg)?\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*[\d.]+\s*)?\)$/i;

function clamp255(n) {
  return Math.max(0, Math.min(255, n));
}

function parseHex(hex) {
  const h = hex.slice(1);
  if (!/^[0-9a-fA-F]+$/.test(h)) return null;
  if (h.length === 3 || h.length === 4) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }
  if (h.length === 6 || h.length === 8) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  return null;
}

function hueToRgb(p, q, t) {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

function hslToRgb(h, s, l) {
  const hue = (((h % 360) + 360) % 360) / 360;
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: clamp255(Math.round(hueToRgb(p, q, hue + 1 / 3) * 255)),
    g: clamp255(Math.round(hueToRgb(p, q, hue) * 255)),
    b: clamp255(Math.round(hueToRgb(p, q, hue - 1 / 3) * 255)),
  };
}

/**
 * Parses #hex (3/4/6/8 digits), rgb()/rgba() and hsl()/hsla() into an RGB
 * triple. Alpha is ignored for the contrast decision. Anything else (or
 * malformed input) yields null — the caller (contrastTextColor) maps that to
 * the documented '#fff' fallback.
 */
// RGB_RE/HSL_RE's [\d.]+ capture groups tolerate malformed numbers like
// "1.2.3" (multiple dots) — Number(...) on those yields NaN rather than
// throwing, so every component must be finite-checked individually and the
// whole match rejected (-> null -> caller's '#fff' fallback) if any isn't.
// PHP mirror (publishedData.php's wm_parseColorToRgb) enforces the same
// per-component strictness with a regex instead (wm_isStrictNumeric) since
// floatval() silently truncates at the second dot ('1.2.3' -> 1.2) rather
// than producing NaN — without this, TS and PHP disagreed on malformed
// input (final-review I2).
function toFiniteNumber(s) {
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function parseColorToRgb(value) {
  const v = value.trim();
  if (v.startsWith('#')) return parseHex(v);
  const rgbMatch = v.match(RGB_RE);
  if (rgbMatch) {
    const r = toFiniteNumber(rgbMatch[1]);
    const g = toFiniteNumber(rgbMatch[2]);
    const b = toFiniteNumber(rgbMatch[3]);
    if (r === null || g === null || b === null) return null;
    return { r: clamp255(r), g: clamp255(g), b: clamp255(b) };
  }
  const hslMatch = v.match(HSL_RE);
  if (hslMatch) {
    const h = toFiniteNumber(hslMatch[1]);
    const s = toFiniteNumber(hslMatch[2]);
    const l = toFiniteNumber(hslMatch[3]);
    if (h === null || s === null || l === null) return null;
    return hslToRgb(h, s / 100, l / 100);
  }
  return null;
}

function lin(c) {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance (0..1) for an sRGB triple. */
export function relativeLuminance({ r, g, b }) {
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two relative luminances (>= 1). */
export function contrastRatio(l1, l2) {
  const [a, b] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (a + 0.05) / (b + 0.05);
}

/**
 * Picks readable text color for a background color. Decision rule (spec
 * §A1.4): white if its contrast ratio against the background is >= 3:1
 * (large/bold-text threshold); otherwise whichever of white / '#212529' has
 * the higher ratio.
 *
 * The >= 3.0 check compares the ratio rounded to 2 decimals, matching the
 * 2-decimal precision the spec's own reference vectors are stated in
 * (§A1: "#0099ff -> weiss (3.00)"). The true unrounded ratio for #0099ff is
 * 2.99978868... (a hair under 3.0, not a floating-point artifact — it is
 * the exact WCAG value), which would otherwise fall through to the
 * higher-ratio branch and flip to dark. Rounding to the same precision the
 * spec computed its vectors at keeps this documented boundary case white,
 * as §A1's vector table requires.
 */
export function contrastTextColor(color) {
  const rgb = parseColorToRgb(color);
  if (!rgb) return '#fff'; // documented fallback, mirrors the old silent (0,0,0) path
  const L = relativeLuminance(rgb);
  const ratioWhite = contrastRatio(1, L);
  const ratioDark = contrastRatio(L, relativeLuminance(DARK_RGB));
  if (Math.round(ratioWhite * 100) / 100 >= 3) return '#fff'; // 3:1 white preference (spec §A1.4)
  return ratioWhite >= ratioDark ? '#fff' : DARK;
}

/**
 * Readable text at WCAG AA (4.5:1) for Bereiche (spec 2026-10-03 Lesbarkeit
 * §3.1): white if white reaches 4.5:1, else '#212529' if that does, else
 * black (black always does once white fails). No rounding: the old 2-digit
 * rounding let 4.495 through. Unparsable -> null (the caller writes no line).
 * The old contrastTextColor stays for V1/V2. PHP mirror:
 * publishedData.php#wm_contrastTextColorAA.
 */
export function contrastTextColorAA(color) {
  const rgb = parseColorToRgb(color);
  if (!rgb) return null;
  const L = relativeLuminance(rgb);
  if (contrastRatio(1, L) >= 4.5) return '#ffffff';
  if (contrastRatio(L, relativeLuminance(DARK_RGB)) >= 4.5) return DARK;
  return '#000000';
}
